import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { HelpCircle } from "lucide-react";
import React, { useState } from "react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CreateDatabaseRequest,
  DatabaseClass,
  DatabaseType,
} from "@/types/database";
import { AlertCircle, Database, HelpCircle as HelpCircleIcon, Plus, X } from "lucide-react";

interface DatabaseFormProps {
  onSubmit: (data: CreateDatabaseRequest) => Promise<void>;
  isLoading?: boolean;
  setShowForm: (show: boolean) => void;
}

const DatabaseForm: React.FC<DatabaseFormProps> = ({
  onSubmit,
  isLoading = false,
  setShowForm,
}) => {
  const [formData, setFormData] = useState<CreateDatabaseRequest>({
    db: "MYSQL",
    dbname: "",
    requireShadowDb: false,
    databaseClass: "DEV",
    tags: [],
  });

  const [tagInput, setTagInput] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { toast } = useToast();

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.dbname.trim()) {
      newErrors.dbname = "Database name is required";
    } else if (formData.dbname.length < 1 || formData.dbname.length > 32) {
      newErrors.dbname = "Database name must be between 1 and 32 characters";
    } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.dbname)) {
      newErrors.dbname =
        "Database name can only contain letters, numbers, underscores, and hyphens";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
      // Reset form on success
      setFormData({
        db: "MYSQL",
        dbname: "",
        requireShadowDb: false,
        databaseClass: "DEV",
        tags: [],
      });
      setTagInput("");
      setErrors({});
    } catch (error) {
      // toast({
      //   title: "form submission failed",
      //   description: "something went wrong try again later",
      //   status: "error",
      // });
    }
  };

  const handleInputChange = (
    field: keyof CreateDatabaseRequest,
    value: any
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()],
      }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags?.filter((tag) => tag !== tagToRemove) || [],
    }));
  };

  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const databaseTypes: { value: DatabaseType; label: string; icon: string }[] =
    [
      { value: "MYSQL", label: "MySQL", icon: "🐬" },
      { value: "POSTGRES", label: "PostgreSQL", icon: "🐘" },
      { value: "MONGODB", label: "MongoDB", icon: "🍃" },
    ];

  const databaseClasses: {
    value: DatabaseClass;
    label: string;
    description: string;
  }[] = [
    {
      value: "DEV",
      label: "Development",
      description: "For development and testing",
    },
    {
      value: "PROD",
      label: "Production",
      description: "For production workloads",
    },
  ];

  return (
    <Card className="w-full max-w-2xl mx-auto max-h-[90vh] overflow-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plus className="h-5 w-5" />
          Create New Database
        </CardTitle>
        <CardDescription>
          Create a new database with automated user provisioning and security
          settings.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Database Type */}
          <div className="space-y-2">
            <label htmlFor="db" className="text-sm font-medium">
              Database Type
            </label>
            <Select
              value={formData.db}
              onValueChange={(value: DatabaseType) =>
                handleInputChange("db", value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select database type" />
              </SelectTrigger>
              <SelectContent>
                {databaseTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <span className="flex items-center gap-2">
                      <span>{type.icon}</span>
                      {type.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Database Class */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label htmlFor="databaseClass" className="text-sm font-medium">
                Database Class
              </label>
              <div className="group relative">
                <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                  Database class determines the environment type. This cannot be
                  changed after creation.
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            </div>
            <Select
              value={formData.databaseClass}
              onValueChange={(value: DatabaseClass) =>
                handleInputChange("databaseClass", value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select database class" />
              </SelectTrigger>
              <SelectContent>
                {databaseClasses.map((dbClass) => (
                  <SelectItem key={dbClass.value} value={dbClass.value}>
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{dbClass.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {dbClass.description}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Database Name */}
          <div className="space-y-2">
            <label htmlFor="dbname" className="text-sm font-medium">
              Database Name
            </label>
            <Input
              id="dbname"
              type="text"
              placeholder="myapp"
              value={formData.dbname}
              onChange={(e) => handleInputChange("dbname", e.target.value)}
              className={errors.dbname ? "border-red-500" : ""}
            />
            {errors.dbname && (
              <div className="flex items-center gap-1 text-sm text-red-500">
                <AlertCircle className="h-4 w-4" />
                {errors.dbname}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Use only letters, numbers, underscores, and hyphens
            </p>
            {formData.databaseClass == "DEV" && (
              <div className="flex items-start space-x-3 pt-2">
                <input
                  type="checkbox"
                  id="requireShadowDb"
                  checked={formData.requireShadowDb}
                  onChange={(e) =>
                    handleInputChange("requireShadowDb", e.target.checked)
                  }
                  className="mt-0.5 rounded border-gray-300"
                />
                <label htmlFor="requireShadowDb" className="text-sm flex items-start gap-2 cursor-pointer">
                  <span>Do you need Prisma support with this DB</span>
                  <a
                    target="_blank"
                    href="https://www.prisma.io/docs/orm/prisma-migrate/understanding-prisma-migrate/shadow-database"
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                  >
                    <HelpCircleIcon className="h-3 w-3" />
                  </a>
                </label>
              </div>
            )}
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <label htmlFor="tags" className="text-sm font-medium">
              Tags (Optional)
            </label>
            <div className="relative">
              <Input
                id="tags"
                type="text"
                placeholder="Add tags (press Enter to add)..."
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleTagKeyPress}
                className="pr-8"
              />
              {tagInput.trim() && (
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-blue-600 hover:text-blue-800"
                >
                  <Plus className="h-4 w-4" />
                </button>
              )}
            </div>
            {formData.tags && formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.tags.map((tag, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm"
                  >
                    <span>{tag}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Add tags to organize and categorize your databases
            </p>
          </div>

          {/* Submit Button */}
          <Button type="submit" disabled={isLoading} className="w-full">
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Creating Database...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4" />
                Create Database
              </div>
            )}
          </Button>
          <Button
            onClick={() => setShowForm(false)}
            disabled={isLoading}
            variant="secondary"
            className="w-full"
          >
            <div className="flex items-center justify-center gap-2">
              <X className="h-4 w-4" />
              <span>Close</span>
            </div>
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default DatabaseForm;
